import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getWithTokenAPI } from "../../util/Api";
import {
  institution_dashboard,
  batch_get_all,
  students_list_approved,
  students_list_autoapproved,
} from "../../util/Apiendpoits";
import { SafeToast } from "../Toast/ShowToast";
import Firstphoto from "../../assets/uploadpage1.png";
import Scoundphoto from "../../assets/uploadpage2.png";
import Thirdphoto from "../../assets/uploadpage3.png";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import "../../styles/upload.css";

const COLORS = ["#059669", "#2563eb", "#8b5cf6", "#f59e0b", "#ec4899", "#3b82f6"];

const InstitutionDashboardComponent = () => {
  const { token, roleType, userId } = useSelector((state) => state.auth);
  const profile = useSelector((state) => state.profile);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryInstId = searchParams.get("institutionId");
  const targetInstitutionId = queryInstId || userId;
  const [institutionProfile, setInstitutionProfile] = useState(null);

  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    totalBatches: 0,
    totalRevenue: 0,
  });

  const [monthlyRevenueData, setMonthlyRevenueData] = useState([]);
  const [revenueByExamData, setRevenueByExamData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    if (!targetInstitutionId) return;
    setLoading(true);
    try {
      if (queryInstId) {
        try {
          const profRes = await getWithTokenAPI(token, `/user/profile/${queryInstId}`);
          if (profRes?.data) {
            setInstitutionProfile(profRes.data);
          }
        } catch (e) {
          console.error("Failed to load institution profile:", e);
        }
      }

      // 1. Fetch Basic Dashboard Stats & Batches in parallel to strictly synchronize count
      const [resStats, resBatches] = await Promise.all([
        getWithTokenAPI(token, `${institution_dashboard}${targetInstitutionId}`),
        getWithTokenAPI(token, `${batch_get_all}${targetInstitutionId}&all=true`).catch(() => null),
      ]);

      let calculatedRevenue = 0;
      let monthlyData = [];
      let examData = [];

      // Calculate distinct active batches strictly matching the Batches listing table
      let batchCount = resStats?.data?.totalBatches || 0;
      if (resBatches?.statusCode === 200 && Array.isArray(resBatches?.data?.data)) {
        const uniqueBatchNames = new Set();
        resBatches.data.data.forEach((item) => {
          const name = item.testBatchName ? item.testBatchName.trim() : "";
          if (name) {
            uniqueBatchNames.add(name);
          }
        });
        batchCount = uniqueBatchNames.size;
      }

      // 2. Fetch Subscription APIs for Revenue and Charts
      try {
        const instId = targetInstitutionId;
        const autoApprovedUrl = `${students_list_autoapproved}&institutionId=${instId}&limit=1000`;
        const approvedUrl = `${students_list_approved}&institutionId=${instId}&limit=1000`;

        const [resAuto, resApproved] = await Promise.all([
          getWithTokenAPI(token, autoApprovedUrl),
          getWithTokenAPI(token, approvedUrl),
        ]);

        const autoList = Array.isArray(resAuto?.data) ? resAuto.data : [];
        const approvedList = Array.isArray(resApproved?.data) ? resApproved.data : [];

        // Use ALL records from BOTH APIs — no deduplication for revenue
        // Each transaction is a payment so every record's amount must be counted
        const allItems = [...autoList, ...approvedList];
        const monthlyMap = {};
        const examMap = {};

        // Calculate distinct active students strictly matching the Total Students table
        const uniqueStudentIds = new Set();
        allItems.forEach((item) => {
          const uId = item.userId?._id || item._id;
          if (uId) {
            uniqueStudentIds.add(uId.toString());
          }

          const price = Number(item.testPrice) || 0;
          calculatedRevenue += price;

          const exam = item.batchId?.examType?.value || "Other";
          examMap[exam] = (examMap[exam] || 0) + price;

          const dateObj = new Date(item.autoApprovedAt || item.createdAt || Date.now());
          if (!isNaN(dateObj.getTime())) {
            const mYear = dateObj.toLocaleDateString("en-IN", {
              month: "short",
              year: "numeric",
            });
            monthlyMap[mYear] = (monthlyMap[mYear] || 0) + price;
          }
        });

        monthlyData = Object.keys(monthlyMap).map((month) => ({
          month,
          revenue: monthlyMap[month],
        }));

        examData = Object.keys(examMap).map((exam) => ({
          name: exam,
          revenue: examMap[exam],
        }));

        setDashboardStats({
          totalStudents: uniqueStudentIds.size,
          totalBatches: batchCount,
          totalRevenue: calculatedRevenue,
        });
      } catch (revError) {
        console.error("Dashboard revenue fetch error:", revError);
        setDashboardStats({
          totalStudents: resStats?.data?.totalStudents || 0,
          totalBatches: batchCount,
          totalRevenue: calculatedRevenue,
        });
      }

      setMonthlyRevenueData(monthlyData);
      setRevenueByExamData(examData);
    } catch (error) {
      console.error("Error fetching institution dashboard data:", error);
      SafeToast.error("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (targetInstitutionId) {
      fetchDashboardData();
    }

    const handleFocus = () => {
      if (targetInstitutionId) {
        fetchDashboardData();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [targetInstitutionId, token]);

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto", fontFamily: "'Alata', sans-serif" }}>
      {queryInstId && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
            background: "#f8fafc",
            padding: "14px 20px",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div>
            <span style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", fontWeight: "700", letterSpacing: "0.5px" }}>
              Super Admin Mode • Institution Dashboard
            </span>
            <h3 style={{ margin: "4px 0 0", fontSize: "20px", color: "#0f172a", fontWeight: "700" }}>
              {institutionProfile?.nameofInstitution || "Institution Dashboard"}
            </h3>
          </div>
          <button
            onClick={() => navigate("/institutions")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "9px 18px",
              background: "#fff",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
              color: "#334155",
            }}
          >
            ← Back to Institutions
          </button>
        </div>
      )}
   

      {/* 3 STAT CARDS ROW */}
      <div
        className="stats-row"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "24px",
          marginBottom: "28px",
        }}
      >
        <div
          className="stat-card-upload student"
          style={{ backgroundImage: `url(${Firstphoto})`, cursor: "pointer" }}
          onClick={() =>
            navigate(
              queryInstId
                ? `/institution/students?institutionId=${queryInstId}`
                : "/institution/students"
            )
          }
        >
          <span className="stat-title">Total Students</span>
          <h2>{dashboardStats.totalStudents.toLocaleString()}</h2>
        </div>

        <div
          className="stat-card-upload batch"
          style={{ backgroundImage: `url(${Scoundphoto})`, cursor: "pointer" }}
          onClick={() =>
            navigate(
              queryInstId
                ? `/batches?institutionId=${queryInstId}`
                : "/batches"
            )
          }
        >
          <span className="stat-title">Total Batches</span>
          <h2>{dashboardStats.totalBatches.toLocaleString()}</h2>
        </div>

        <div
          className="stat-card-upload revenue"
          style={{ backgroundImage: `url(${Thirdphoto})`, cursor: "pointer" }}
          onClick={() =>
            navigate(
              queryInstId
                ? `/institution/revenue?institutionId=${queryInstId}`
                : "/institution/revenue"
            )
          }
        >
          <span className="stat-title">Total Revenue</span>
          <h2>₹{dashboardStats.totalRevenue.toLocaleString()}</h2>
        </div>
      </div>

      {/* ANALYTICS CHARTS ROW */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "24px",
          marginBottom: "28px",
        }}
      >
        {/* Monthly Revenue Bar Chart */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            padding: "24px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <h3 style={{ margin: "0 0 16px 0", fontSize: "17px", color: "#0f172a", fontWeight: "600" }}>
            Monthly Revenue Growth (₹)
          </h3>
          {monthlyRevenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]}
                  contentStyle={{ borderRadius: "8px", border: "1px solid #cbd5e1" }}
                />
                <Bar dataKey="revenue" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: "50px", textAlign: "center", color: "#94a3b8" }}>No monthly revenue data available</div>
          )}
        </div>

        {/* Revenue Breakdown by Exam Type Pie Chart */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            padding: "24px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <h3 style={{ margin: "0 0 16px 0", fontSize: "17px", color: "#0f172a", fontWeight: "600" }}>
            Revenue Breakdown by Exam Type
          </h3>
          {revenueByExamData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={revenueByExamData}
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  dataKey="revenue"
                  label={({ name, revenue }) => `${name}: ₹${revenue}`}
                >
                  {revenueByExamData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: "50px", textAlign: "center", color: "#94a3b8" }}>No exam revenue data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstitutionDashboardComponent;
